import { FBA_PREP_PRICING_FAQS } from './fbaPrepPricingData';
import type { FAQItem } from '../seo/FAQSchema';
import { REMOVAL_ORDER_FAQS } from './removalOrdersData';
import { AMAZON_FBA_PREP_SERVICE_FAQS } from './amazonFbaPrepServiceFaqs';
import { logisticsServiceFaqs } from '../components/Logistics/logisticsFaqData';
import { wmsIntegrationFaqs } from '../components/Logistics/wmsIntegrationFaqData';
import { logisticsClusterConfigs } from '../components/Logistics/logisticsClusterData';
import { HOMEPAGE_FAQS } from './homepageFaqs';
import { MULTI_CHANNEL_FAQS } from './multiChannelFaqs';

/** Amazon FBA prep services page FAQs — source: amazonFbaPrepServiceFaqs.ts */
const amazonFbaPrepServiceFaqs: FAQItem[] = AMAZON_FBA_PREP_SERVICE_FAQS;

const shopifyFulfilmentFaqs: FAQItem[] = [
  {
    question: 'How does Shopify integration work?',
    answer:
      'We connect your Shopify store to our WMS so orders import automatically and inventory updates sync back in real time. No manual file uploads are required.',
  },
  {
    question: 'Do you support real-time order sync?',
    answer:
      'Yes. Orders, tracking and stock levels are synced in near real time, helping prevent overselling and keeping customers informed.',
  },
  {
    question: 'Do you offer branded packaging?',
    answer:
      'Yes. We can store and apply branded boxes, mailers and inserts per your SOPs to keep your unboxing consistent with your brand.',
  },
  {
    question: 'What are your dispatch times?',
    answer:
      'Orders received before the agreed cut-off (often 1–3pm) are dispatched the same day. We track on-time performance and share reports.',
  },
  {
    question: 'How are returns handled?',
    answer:
      'Returns are scanned and graded within 24 hours. Sellable items are restocked the same day; quarantined items follow your rules for rework or disposal.',
  },
  {
    question: 'Which couriers do you use?',
    answer:
      'We use Royal Mail, DPD, DHL, Evri and UPS. Labels are selected by SLA, weight and destination to balance delivery speed and cost.',
  },
  {
    question: 'How quickly can we go live after onboarding?',
    answer: 'Once SKUs are mapped and stock arrives, many brands go live within a few working days.',
  },
];

const sfpServiceFaqs: FAQItem[] = [
  {
    question: 'What are the benefits of Seller Fulfilled Prime?',
    answer:
      'Seller Fulfilled Prime offers significant advantages over standard FBA: you keep the Prime badge to boost sales and customer trust, avoid FBA storage fees and Q4 surge pricing, eliminate FBA restock limitations, get faster customer service by handling issues directly, benefit from lower inbound charges and enjoy omni-channel fulfilment across eBay, TikTok, Shopify and more — all from one warehouse.',
  },
  {
    question: 'Why choose SFP over FBA?',
    answer:
      "SFP is the better choice when you want to avoid FBA restock limits and long check-in delays, cut inbound and storage costs, maintain full control over your inventory and packaging, get faster support without going through Amazon's seller support, avoid Q4 surge pricing and long-term storage penalties, or sell across multiple platforms simultaneously. FBA still makes sense for very high volumes or if you have no warehousing infrastructure — but for growing brands wanting flexibility, SFP with Pick Pack Pro is typically more cost-effective.",
  },
  {
    question: 'How do I get the Prime badge through you?',
    answer:
      "We support you through the full SFP registration and trial process. Once Amazon approves your SFP application (we'll guide you), we fulfil your Prime orders using Amazon's approved carrier templates, provide 1-day or 2-day delivery via Amazon Buy Shipping Services and maintain your SFP performance metrics at 99%+ accuracy and on-time delivery. We process around 2,000 orders per day while consistently meeting Amazon's requirements.",
  },
  {
    question: 'How will you prevent my Prime badge from being removed due to service provider issues?',
    answer:
      "We currently fulfil around 2,000 Prime orders per day while consistently meeting or exceeding Amazon's SFP requirements: 99%+ on-time shipment rate, 98%+ Buy Shipping compliance and a cancellation rate well below Amazon's 0.5% threshold. Our entire operation is built around SFP compliance — it's not an add-on, it's our core speciality.",
  },
  {
    question: 'Do I have to be an enrolled SFP seller already to work with you?',
    answer:
      "No. If you're not yet enrolled in Seller Fulfilled Prime, we'll help guide you through the application and trial process. We'll advise on meeting Amazon's eligibility requirements, support you during the trial period and ensure your metrics are hit from day one so you keep the Prime badge long-term.",
  },
];

const fbmFaqs: FAQItem[] = [
  {
    question: 'What performance metrics do you meet for Amazon FBM?',
    answer:
      'We operate at 99%+ on‑time shipment, 98%+ Buy Shipping compliance and under 0.5% cancellations, with daily reporting and exception reviews.',
  },
  {
    question: 'How fast do you upload tracking for FBM orders?',
    answer:
      'Tracking uploads immediately after label generation to protect Valid Tracking Rate and buyer notifications.',
  },
  {
    question: 'Do you support weekend dispatch for FBM?',
    answer:
      'Yes. We dispatch seven days a week within agreed cut‑offs to keep delivery promises consistent.',
  },
  {
    question: 'Can you handle peak volume without missing SLAs?',
    answer:
      'Yes. We scale packing pods, batch by SKU and extend shifts during peaks like Q4 and promos while holding metrics.',
  },
  {
    question: 'Do you purchase labels via Amazon Buy Shipping?',
    answer:
      'Yes. Labels are purchased via Buy Shipping to maintain compliance and ensure tracking is registered correctly.',
  },
  {
    question: 'What is your returns processing SLA for FBM?',
    answer:
      'Returns are scanned and graded within 24–48 hours with reason codes and sellable items are restocked promptly.',
  },
];

const returnsFaqs: FAQItem[] = REMOVAL_ORDER_FAQS;

// ---------------------------------------------------------------------------
// NEW FAQ SETS — covering previously zero-coverage routes
// ---------------------------------------------------------------------------

/** /amazon-fba-prep-wholesale */
const wholesaleFbaPrepFaqs: FAQItem[] = [
  {
    question: 'What is wholesale FBA prep?',
    answer:
      'Wholesale FBA prep is the process of receiving bulk supplier cartons, verifying quantities, applying FNSKU labels, polybagging and building compliant Amazon inbound shipments — so large volumes of stock move from supplier to Amazon FC without Amazon rejecting them.',
  },
  {
    question: 'What volumes do you handle for wholesale FBA prep?',
    answer:
      'Pick Pack Pro scales from single-carton test shipments to high-throughput wholesale inbound. There is no long-term minimum — pricing tiers reduce per-unit cost as volumes grow, so you can start lean and expand without switching providers.',
  },
  {
    question: 'How fast is turnaround for wholesale FBA prep?',
    answer:
      'Standard turnaround is 48 hours from goods-in to dispatch-ready cartons once SKUs are mapped. Rush prep is available for launch or restock deadlines — speak to your account manager to arrange.',
  },
  {
    question: 'What is included in wholesale FBA prep services?',
    answer:
      'Typically: goods-in receiving, quantity and condition checks, FNSKU labelling, polybagging, box content labels, cartonisation and compliant shipment booking to Amazon fulfilment centres — all aligned with current Seller Central inbound requirements.',
  },
  {
    question: 'Can you handle supplier-direct deliveries for wholesale FBA prep?',
    answer:
      'Yes. UK suppliers, importers and aggregators regularly deliver pallets directly to our Milton Keynes facility. We receive, inspect and prep to Amazon UK requirements, then forward to the correct FC.',
  },
];

/** /amazon-fba-prep-private-label */
const privateLabelFbaPrepFaqs: FAQItem[] = [
  {
    question: 'What is private label FBA prep?',
    answer:
      'Private label FBA prep covers branded product workflows — FNSKU labelling, branded packaging, polybagging, bundling and QC checks so your PL inventory meets Amazon inbound requirements and arrives at the fulfilment centre ready to sell.',
  },
  {
    question: 'Can you handle branded packaging and inserts for private label products?',
    answer:
      'Yes. We store and apply your branded boxes, mailers and inserts per your SOPs. Pack-out is documented for consistency across repeat orders and catalogue expansions.',
  },
  {
    question: 'How do you handle QC for private label FBA prep?',
    answer:
      'Incoming stock is inspected for quantity, condition and labelling accuracy before prep begins. Any defects are flagged to you with photos before we proceed — protecting your seller metrics from bad stock reaching FCs.',
  },
  {
    question: 'Do you support bundling for private label Amazon listings?',
    answer:
      'Yes. We kit multi-unit bundles and virtual bundles to your Bill of Materials, apply bundle barcodes and ensure carton contents match Seller Central shipment plans.',
  },
  {
    question: 'Where is your private label FBA prep centre?',
    answer:
      'Unit 36 Tanners Drive, Blakelands, Milton Keynes MK14 5BN — central UK with easy motorway access for supplier deliveries and FC dispatch.',
  },
];

/** /amazon-fba-shipments */
const fbaShipmentsFaqs: FAQItem[] = [
  {
    question: 'What is an Amazon FBA shipment plan?',
    answer:
      'An FBA shipment plan is a booking you create in Amazon Seller Central that tells Amazon which products, quantities and carton dimensions to expect at its fulfilment centres. Pick Pack Pro handles shipment creation, box labels, SPD/LTL selection and carrier booking on your behalf.',
  },
  {
    question: 'What is the difference between SPD and LTL for Amazon FBA inbound?',
    answer:
      'SPD (Small Parcel Delivery) is used for smaller inbound shipments sent via parcel carriers. LTL (Less Than Truckload) is used for pallet-sized freight. Pick Pack Pro advises which mode fits your volume and Amazon FC routing, then books the correct carrier.',
  },
  {
    question: 'Do you create Amazon shipment plans for me?',
    answer:
      'Yes. We create the Seller Central inbound plan, generate required box labels and carton content files, arrange compliant carrier booking and provide confirmation once shipments are accepted at the FC.',
  },
  {
    question: 'How long does Amazon FBA inbound take from your warehouse?',
    answer:
      'Carrier transit to Amazon UK FCs is typically 1–3 working days depending on destination FC. We dispatch same day for shipments ready before the agreed cut-off.',
  },
  {
    question: 'Can you handle Amazon FBA inbound from international suppliers?',
    answer:
      'Yes. Stock from overseas suppliers arrives at our Milton Keynes facility, we inspect and prep it, then create and dispatch the Amazon inbound shipment — reducing the risk of non-compliant deliveries going direct from factory to FC.',
  },
];

/** /freight-forwarder-uk */
const freightForwarderFaqs: FAQItem[] = [
  {
    question: 'What does a UK freight forwarder do?',
    answer:
      'A UK freight forwarder arranges the collection, transport, customs clearance and delivery of goods — typically from overseas supplier to UK warehouse or fulfilment centre. Pick Pack Pro coordinates inbound freight so stock arrives at our Milton Keynes hub ready for FBA prep or ecommerce fulfilment.',
  },
  {
    question: 'Do you help with UK customs clearance?',
    answer:
      'We coordinate with customs brokers to support import declarations, duty payments and any required documentation so your goods clear UK customs efficiently before arriving at our prep and fulfilment centre.',
  },
  {
    question: 'Can you collect goods directly from my supplier?',
    answer:
      'Yes. We arrange collections from UK suppliers and coordinate inbound from international manufacturers, routing stock into our Milton Keynes facility for receiving, inspection and onward prep or storage.',
  },
  {
    question: 'How does freight forwarding fit with FBA prep?',
    answer:
      'Freight forwarding and FBA prep work together in one flow: goods arrive at our hub, we clear any import requirements, receive and inspect stock, prep to Amazon inbound standards and dispatch compliant shipments to Amazon FCs — under one account manager.',
  },
  {
    question: 'What are the benefits of using a UK freight forwarder with FBA prep?',
    answer:
      "Using a combined UK freight forwarder and prep centre reduces handling steps, shortens lead times and lowers the risk of non-compliant stock reaching Amazon's fulfilment centres. It also simplifies returns: damaged or rejected stock comes back to the same facility for inspection and rework.",
  },
];

/** /contract-packing */
const contractPackingFaqs: FAQItem[] = [
  {
    question: 'What is contract packing?',
    answer:
      'Contract packing (or co-packing) is outsourcing the physical assembly, labelling, kitting and packaging of products to a specialist facility. Pick Pack Pro handles contract packing at scale from our Milton Keynes warehouse — including retail-ready packs, bundles and promotional sets.',
  },
  {
    question: 'What contract packing services do you offer?',
    answer:
      'We offer kitting and bundling, FNSKU and retail barcode labelling, polybagging, shrink-wrapping, assembly of multi-component products, insertion of leaflets and promotional materials, and final cartonisation ready for Amazon FCs or retailer deliveries.',
  },
  {
    question: 'What is the minimum order for contract packing?',
    answer:
      'There is no fixed minimum — we quote based on your product type, complexity and volume. Small test runs and high-volume ongoing contracts are both catered for from the same Milton Keynes facility.',
  },
  {
    question: 'Can you produce retail-ready packs for supermarkets or high street retailers?',
    answer:
      'Yes. We pack to retailer specifications including shelf-ready packaging (SRP), pallet displays and compliance labelling for UK and EU retail formats.',
  },
  {
    question: 'How does contract packing connect to fulfilment?',
    answer:
      'Because packing and fulfilment happen under the same roof, packed goods move directly into our pick-and-pack or FBA prep workflow — no inter-warehouse transfers, lower handling cost and faster time to ship.',
  },
];

/** /our-fulfilment-center */
const fulfilmentCentreGeneralFaqs: FAQItem[] = [
  {
    question: 'Where is Pick Pack Pro\'s fulfilment centre?',
    answer:
      'Our fulfilment centre is at Unit 36 Tanners Drive, Blakelands, Milton Keynes, MK14 5BN. Central UK location with strong motorway access for nationwide next-day delivery coverage and efficient carrier collections.',
  },
  {
    question: 'What services does your fulfilment centre offer?',
    answer:
      'Our Milton Keynes fulfilment centre handles ecommerce pick and pack, Amazon FBA prep, Seller Fulfilled Prime, TikTok Shop and Shopify fulfilment, B2B wholesale dispatch, warehousing and returns processing — all from one site under a unified WMS.',
  },
  {
    question: 'What hours does the fulfilment centre operate?',
    answer:
      'The facility operates Monday to Friday 09:00–18:00. Weekend dispatch and extended cut-off SLAs are available for Seller Fulfilled Prime and other agreed programmes.',
  },
  {
    question: 'Do you offer warehouse tours?',
    answer:
      'Yes. Prospective clients are welcome to visit our Milton Keynes facility to see the pick-and-pack stations, FBA prep area, WMS and dispatch operations. Contact us to arrange a visit.',
  },
  {
    question: 'How does your WMS track stock at the fulfilment centre?',
    answer:
      'We use Mintsoft WMS with barcode scanning at every touchpoint — receiving, put-away, pick, pack and dispatch. You have visibility of live stock levels, order status and inventory movements through your client dashboard.',
  },
];

/** /fulfilment-centre-milton-keynes */
const mkFulfilmentFaqs: FAQItem[] = [
  {
    question: 'Why is Milton Keynes a good location for a UK fulfilment centre?',
    answer:
      'Milton Keynes sits at the geographic centre of England with direct access to the M1 motorway. That positioning means same-day carrier collections reach over 90% of UK postcodes for next-day delivery — making it one of the most efficient fulfilment locations in the country.',
  },
  {
    question: 'Which couriers collect from your Milton Keynes warehouse?',
    answer:
      'Royal Mail, DPD, DHL, Evri and UPS all make daily collections from our Milton Keynes site. Carrier selection is automatic based on parcel weight, size, SLA and destination to balance delivery speed against shipping cost.',
  },
  {
    question: 'Can I store stock at your Milton Keynes fulfilment centre?',
    answer:
      'Yes. We offer ecommerce warehousing with barcode receiving, secure storage and fulfilment-ready inventory. Stock is available for picking from the moment it is booked into the WMS after inspection.',
  },
  {
    question: 'How do I get stock into the Milton Keynes fulfilment centre?',
    answer:
      'You book an inbound delivery via your account manager or our WMS portal, send an ASN (Advance Shipment Notice), then your supplier or freight provider delivers directly to our Blakelands site. We receive, count and inspect on arrival.',
  },
  {
    question: 'Is Pick Pack Pro\'s Milton Keynes site suitable for Amazon FBA prep?',
    answer:
      'Yes. Our Milton Keynes facility has a dedicated FBA prep area with FNSKU labelling, polybagging, bundling and carton preparation capabilities. It is our primary prep centre for UK Amazon sellers sending inbound to FCs.',
  },
];

/** /book-a-free-call */
const bookCallFaqs: FAQItem[] = [
  {
    question: 'What happens on the free consultation call?',
    answer:
      'In 15–30 minutes we cover your order volumes, SKU profile, current fulfilment challenges and which of our services — FBA prep, SFP, Shopify fulfilment or pick and pack — is the best fit. No obligation, no hard sell.',
  },
  {
    question: 'How quickly will I get a quote after the call?',
    answer:
      'Most clients receive an itemised quote within 24–48 hours of the call. We model your specific volumes and SKU mix rather than issuing generic rate cards.',
  },
  {
    question: 'Who will I be speaking to on the call?',
    answer:
      'You will speak directly with a member of our operations team — not a sales script. We understand fulfilment operations and can answer technical questions about FBA prep standards, SFP compliance and WMS integrations.',
  },
];

// ---------------------------------------------------------------------------
// CONVERSATIONAL GSC-QUERY FAQs — definition-style questions matching
// impression queries from Search Console: "what is seller fulfilled prime",
// "what does amazon fba do", "what is the fee for amazon fba uk",
// "how to choose a fulfillment partner"
// ---------------------------------------------------------------------------

/** Extra definition FAQs added to SFP page to match GSC conversational queries. */
const sfpDefinitionFaqs: FAQItem[] = [
  {
    question: 'What is Seller Fulfilled Prime?',
    answer:
      "Seller Fulfilled Prime (SFP) is an Amazon programme that lets third-party sellers display the Prime badge on their listings while fulfilling orders from their own warehouse — or a 3PL like Pick Pack Pro — rather than Amazon's fulfilment centres. You keep the Prime badge and avoid FBA storage fees.",
  },
  {
    question: 'How does Seller Fulfilled Prime work?',
    answer:
      'To qualify for SFP, sellers must meet Amazon Prime delivery standards (next-day or 2-day), purchase labels via Amazon Buy Shipping, maintain a 99%+ on-time shipment rate and keep cancellations below 0.5%. Pick Pack Pro manages all of this from our Milton Keynes warehouse on your behalf.',
  },
];

/** Extra definition FAQs added to FBA prep services page to match GSC queries. */
const fbaDefinitionFaqs: FAQItem[] = [
  {
    question: 'What does Amazon FBA do?',
    answer:
      "Amazon FBA (Fulfilled by Amazon) is a service where Amazon stores your products in its fulfilment centres and handles picking, packing, shipping, customer service and returns on your behalf. You send prepped inventory in — Amazon ships it out to buyers under the Prime promise.",
  },
  {
    question: 'What is the cost of Amazon FBA in the UK?',
    answer:
      'Amazon FBA fees in the UK consist of a per-unit fulfilment fee (based on size and weight), monthly storage fees, optional removal and disposal fees, and potential long-term storage fees. Additionally, using a FBA prep centre for labelling and polybagging costs from £0.08–£0.30 per unit depending on the task.',
  },
  {
    question: 'How do you choose a fulfilment partner in the UK?',
    answer:
      'When choosing a UK fulfilment partner, check: their experience with your sales channels (Amazon, Shopify, TikTok Shop), picking accuracy rates, real turnaround times, pricing transparency (per-unit not per-pallet), their WMS integrations, SFP compliance capability, and whether they have genuine client reviews. Ask for a facility tour before committing.',
  },
];

/** Extra conversational FAQs for FBA prep pricing page. */
const fbaPricingConversationalFaqs: FAQItem[] = [
  {
    question: 'What is the fee for Amazon FBA prep in the UK?',
    answer:
      'Amazon FBA prep fees in the UK typically run £0.08–£0.30 per unit depending on the task: FNSKU labelling from £0.08, polybagging from £0.12, bubble wrap from £0.15, kitting from £0.25. Receiving and inspection are itemised separately. Volume discounts apply as monthly throughput grows.',
  },
  {
    question: 'Is FBA prep cost worth it?',
    answer:
      'FBA prep is worth the cost because non-compliant inbound shipments get rejected or fined by Amazon, creating much higher costs. A reliable UK prep centre with 24–48h turnaround protects your inventory flow and seller account health — the per-unit cost is small relative to the risk of FBA non-compliance.',
  },
];

const pricingFaqs: FAQItem[] = [
  {
    question: 'How are fulfilment prices calculated?',
    answer:
      'Pricing is based on pick/pack, additional items, packaging materials, storage and courier label. We model your basket and parcel sizes to forecast landed cost accurately.',
  },
  {
    question: 'Do you charge setup fees or long contracts?',
    answer:
      'No long lock‑ins. Setup is typically free for standard integrations. We operate rolling monthly terms.',
  },
  {
    question: 'Can I get volume discounts?',
    answer:
      'Yes. Tiers reduce per‑order costs as your monthly volume grows. Speak to us for a custom quote.',
  },
  {
    question: 'Are packaging materials included?',
    answer:
      'Materials can be bundled or itemised depending on your preference. We recommend right‑sizing to lower postage bands.',
  },
  {
    question: 'What about returns handling fees?',
    answer:
      'Returns are scanned and graded within 24–48h. Fees are itemised per unit for transparency and control.',
  },
  {
    question: 'Do you support Amazon SFP rate compliance?',
    answer:
      'Yes—labels are purchased via Amazon Buy Shipping and tracking uploads immediately to protect performance metrics.',
  },
];

const tikTokShopFaqs: FAQItem[] = [
  {
    question: 'How does Pick Pack Pro connect to my TikTok Shop for UK orders?',
    answer:
      'We integrate with TikTok Shop so orders flow into our WMS automatically: new orders import in near real time, inventory levels sync back to TikTok to reduce oversells and carrier tracking is pushed as soon as labels are generated—protecting your TikTok seller performance metrics.',
  },
  {
    question: 'Can you handle TikTok Shop order spikes when a product video goes viral?',
    answer:
      'Yes. Viral catalogue traffic is the same operational problem as live spikes: we batch by SKU, add packing capacity and prioritise TikTok Shop lines that are trending so dispatch stays within your agreed cut-offs without manual firefighting.',
  },
  {
    question: 'What dispatch cut-off do you offer for TikTok Shop orders in the UK?',
    answer:
      'We offer same-day dispatch for TikTok Shop orders received before the agreed cut-off (typically early afternoon, confirmed in your SLA). Late orders roll to the next working day with clear rules so you can set accurate handling times in Seller Centre.',
  },
  {
    question: 'How quickly is tracking sent back to TikTok Shop after dispatch?',
    answer:
      "Tracking is submitted right after label generation and handover to the carrier, so buyers see updates promptly and TikTok's systems receive proof of dispatch in line with marketplace expectations.",
  },
  {
    question: 'Do you process TikTok Shop returns and refunds workflows?',
    answer:
      'Yes. Returns arrive at our Milton Keynes facility, are scanned and graded within 24–48 hours, then restocked, quarantined or disposed of per your SOPs—so TikTok Shop return reasons and inventory accuracy stay aligned.',
  },
  {
    question: 'Can TikTok Shop share one inventory pool with Shopify and Amazon?',
    answer:
      'Yes. Many brands hold a single UK stock pool with us and fulfil TikTok Shop, Shopify, Amazon (FBM/SFP) and other channels from the same SKUs—reducing split inventory and stockouts while keeping channel-specific labels and packing rules.',
  },
  {
    question: 'Do you support TikTok Shop bundles, kits and branded packaging?',
    answer:
      'Yes. We kit to your BOM, apply branded boxes or inserts where allowed and document pack-out so TikTok Shop listings, barcodes and carton contents stay consistent for audits and repeat orders.',
  },
  {
    question: 'How fast can we go live on TikTok Shop fulfilment with Pick Pack Pro?',
    answer:
      'Most brands launch within a few working days after channel connection tests, SKU mapping, pack SOP sign-off and first inbound receipt—faster if master data and barcodes are already clean.',
  },
];

const tikTokLiveFaqs: FAQItem[] = [
  {
    question: 'How do you handle viral TikTok order spikes?',
    answer:
      'We scale operations instantly with dedicated picking teams, batching by SKU and automated label workflows to clear spikes fast.',
  },
  {
    question: 'What is your dispatch cut-off?',
    answer:
      'Same-day dispatch for orders placed before the agreed cutoff (often 1–3pm), with on-time performance reporting.',
  },
  {
    question: 'How does TikTok Shop integration work?',
    answer:
      'Yes. We connect via real-time API so orders import automatically, inventory syncs back and tracking updates post immediately.',
  },
  {
    question: 'Can you manage viral campaigns?',
    answer:
      'Yes. We pre-stage hot SKUs, expand packing pods and run wave picking to sustain high throughput during viral surges.',
  },
  {
    question: 'Do you support returns?',
    answer:
      'Yes. Returns are scanned and graded within 24–48 hours, with restock or quarantine per your SOPs.',
  },
  {
    question: 'How is tracking updated?',
    answer:
      'Tracking is uploaded right after label generation to protect seller metrics and customer notifications.',
  },
];

const prepCentreServiceFaqs: FAQItem[] = [
  {
    question: 'How much does Amazon FBA prep cost in the UK?',
    answer:
      'Most prep tasks are quoted per unit. At Pick Pack Pro, FNSKU labelling starts from £0.08, poly bagging from £0.12, bubble wrapping from £0.15 and kitting from £0.25, with volume discounts as throughput grows. Receiving and inspection are itemised transparently — see our full FBA prep pricing page for tiered rates.',
  },
  {
    question: 'How fast is FBA prep centre UK turnaround?',
    answer:
      'Standard SLA is 48 hours from goods-in to dispatch-ready cartons once SKUs are mapped and stock is booked in. Same-day receiving is available for inbound arriving before the agreed cut-off, and rush prep can be arranged for launch or restock deadlines.',
  },
  {
    question: 'Do I need a UK-based prep centre for Amazon FBA UK?',
    answer:
      'If you sell on Amazon UK or EU marketplaces, a UK prep centre reduces inbound lead time, simplifies returns handling and keeps inventory inside the same regulatory and carrier network. International sellers often route EU-bound stock through a UK prep centre before Amazon FC delivery.',
  },
  {
    question: "What's included in Amazon FBA prep services?",
    answer:
      'Typical workflows include goods-in receiving, quantity and condition checks, FNSKU labelling, poly bagging, bubble wrap, bundling, box content labels, cartonisation and booking compliant shipments to Amazon fulfilment centres — all aligned with current Seller Central inbound rules.',
  },
  {
    question: 'Can international sellers ship stock to a UK FBA prep centre?',
    answer:
      'Yes. Overseas suppliers and aggregators regularly send cartons or pallets to our Milton Keynes facility. We receive, inspect and prep to Amazon UK requirements, then forward to the correct FC — useful for US, EU and APAC brands entering the UK marketplace.',
  },
  {
    question: 'Is there a minimum order for FBA prep in the UK?',
    answer:
      'No long-term minimum commitment is required. We prep single-carton test shipments through to high-volume wholesale inbound. Pricing scales with volume, so smaller sellers can start lean and grow without switching providers.',
  },
  {
    question: 'Where is your Amazon FBA prep centre located?',
    answer:
      'Unit 36 Tanners Drive, Blakelands, Milton Keynes MK14 5BN — central UK with strong motorway access for nationwide seller inbound and outbound to Amazon FCs.',
  },
];

const ROUTE_FAQS: Record<string, FAQItem[]> = {
  '/': HOMEPAGE_FAQS,
  '/amazon-fba-prep-services': [
    ...amazonFbaPrepServiceFaqs,
    ...fbaDefinitionFaqs,
  ],
  '/amazon-fba-prep-centre-services': prepCentreServiceFaqs,
  '/amazon-fba-prep-wholesale': wholesaleFbaPrepFaqs,
  '/amazon-fba-prep-private-label': privateLabelFbaPrepFaqs,
  '/amazon-fba-shipments': fbaShipmentsFaqs,
  '/amazon-fba-prep-pricing': [
    ...FBA_PREP_PRICING_FAQS,
    ...fbaPricingConversationalFaqs,
  ],
  '/shopify-fulfillment-services': shopifyFulfilmentFaqs,
  '/3pl-pricing-explained': [
    {
      question: 'What is 3PL pricing UK typically made of?',
      answer:
        'Most UK 3PL pricing combines pick and pack fees (first item + additional lines), storage (bin or pallet), packing materials and courier labels. Transparent partners itemise each line so you can model landed cost per order.',
    },
    {
      question: 'How does Pick Pack Pro 3PL pricing compare to Amazon FBA fees?',
      answer:
        'FBA bundles fulfilment and storage into Amazon’s schedule with long-term storage risk. 3PL pricing UK is usually lower and more predictable for multichannel brands — especially medium/large items — but you (or your 3PL) own Prime SLAs if you run SFP.',
    },
    {
      question: 'Do you publish 3PL pricing without a sales call?',
      answer:
        'Yes. Use our fulfilment pricing calculator and this 3PL pricing explained page for illustrative ranges, then request an itemised quote for your SKU profile and volumes.',
    },
  ],
  '/logistics-services': logisticsServiceFaqs,
  '/ecommerce-fulfillment-services': logisticsClusterConfigs.ecommerce.faqs,
  '/b2b-fulfillment': logisticsClusterConfigs.b2b.faqs,
  '/warehousing-services': logisticsClusterConfigs.warehousing.faqs,
  '/wms-integrations': wmsIntegrationFaqs,
  '/seller-fulfilled-prime-services': [
    ...sfpDefinitionFaqs,
    ...sfpServiceFaqs,
  ],
  '/multi-channel-fulfillment-services': MULTI_CHANNEL_FAQS,
  // Bug fix: fbmFaqs was defined but missing from ROUTE_FAQS — now registered.
  '/fulfillment-by-merchant-services': fbmFaqs,
  '/return-handling-services': returnsFaqs,
  '/pricing-fulfilment-services': pricingFaqs,
  '/tiktok-shop-fulfillment-services': tikTokShopFaqs,
  '/tiktok-live-sales-fulfillment': tikTokLiveFaqs,
  '/freight-forwarder-uk': freightForwarderFaqs,
  '/contract-packing': contractPackingFaqs,
  '/our-fulfilment-center': fulfilmentCentreGeneralFaqs,
  '/fulfilment-centre-milton-keynes': mkFulfilmentFaqs,
  '/book-a-free-call': bookCallFaqs,
};

export function getRouteFaqs(pathname: string): FAQItem[] {
  const path =
    pathname.endsWith('/') && pathname.length > 1
      ? pathname.slice(0, -1)
      : pathname || '/';
  return ROUTE_FAQS[path] ?? [];
}
